import { Link } from 'react-router-dom';

type PageHeroProps = {
  eyebrow?: string;
  title: string;
  description: string;
  primaryLabel?: string;
  primaryTo?: string;
  secondaryLabel?: string;
  secondaryTo?: string;
  align?: 'left' | 'center';
  compact?: boolean;
  backgroundImage?: string;
};

import defaultHeroImg from '../../images/VIP House Movers Removal Van.jpg';

const DEFAULT_HERO_IMAGE = defaultHeroImg;

export function PageHero({
  eyebrow = 'VIP House Movers',
  title,
  description,
  primaryLabel = 'Get a Free Quote',
  primaryTo = '/quote',
  secondaryLabel = 'Speak to Our Team',
  secondaryTo = '/contact',
  align = 'left',
  compact = false,
  backgroundImage = DEFAULT_HERO_IMAGE,
}: PageHeroProps) {
  const isCentered = align === 'center';

  return (
    <section
      className={`relative overflow-hidden border-b border-white/10 bg-gray-900 text-white ${
        compact ? 'pt-28 pb-16 md:pt-32 md:pb-20' : 'pt-32 pb-20 md:pt-36 md:pb-24'
      }`}
    >
      {/* Background image */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url('${backgroundImage}')` }}
        aria-hidden="true"
      />
      {/* Hidden image for search engines and accessibility */}
      <img
        src={backgroundImage}
        alt={`${title} - VIP House Movers`}
        className="sr-only"
      />
      {/* Dark gradient overlay for readability */}
      <div className="absolute inset-0 bg-[linear-gradient(135deg,rgba(17,24,39,0.94)_0%,rgba(17,24,39,0.82)_55%,rgba(31,41,55,0.72)_100%)]" />
      {/* Brand accent glows */}
      <div className="absolute inset-0 opacity-25 bg-[radial-gradient(circle_at_top_right,_rgba(15,74,78,0.42),_transparent_35%),radial-gradient(circle_at_bottom_left,_rgba(255,255,255,0.08),_transparent_28%)]" />
      {/* Top accent hairline */}
      <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-[#0F4A4E]/60 to-transparent" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className={`${isCentered ? 'mx-auto max-w-5xl text-center' : 'max-w-4xl'}`}>
          <div
            className={`inline-flex items-center gap-2 rounded-full border border-[#B7D1D2] bg-[#E7F2F2]/90 px-4 py-2 text-sm font-semibold uppercase tracking-[0.2em] text-[#0F4A4E] shadow-lg shadow-[#0F4A4E]/5 backdrop-blur-sm ${
              isCentered ? 'mx-auto' : ''
            }`}
          >
            {eyebrow}
          </div>
          <h1 className="mt-6 text-5xl font-bold tracking-tight leading-tight md:text-6xl">
            {title}
          </h1>
          <p
            className={`mt-6 text-xl font-light leading-relaxed text-gray-200 ${
              isCentered ? 'mx-auto max-w-3xl' : 'max-w-3xl'
            }`}
          >
            {description}
          </p>
          <div className={`mt-10 flex flex-col gap-4 sm:flex-row ${isCentered ? 'justify-center' : ''}`}>
            <Link
              to={primaryTo}
              className="inline-flex items-center justify-center gap-3 rounded-xl bg-[#0F4A4E] px-8 py-4 text-lg font-bold text-white transition-all duration-300 hover:scale-105 hover:bg-[#13595E] shadow-lg shadow-[#0F4A4E]/20"
            >
              {primaryLabel}
            </Link>
            <Link
              to={secondaryTo}
              className="inline-flex items-center justify-center gap-3 rounded-xl border border-white/20 bg-white/5 px-8 py-4 text-lg font-bold text-white transition-all duration-300 hover:border-[#B7D1D2] hover:text-[#9ED0D3] backdrop-blur-sm"
            >
              {secondaryLabel}
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
